import React, {useEffect, useRef, useState} from 'react';
import {useForm, useService, useToast, useOverlay} from '@dishtech-react/hooks';
import {CrudDataTable, Overlay} from '@dishtech-react/core';
import {GetRippleRoles, areRolesAuthorized} from '@dishtech-react/utils';
import {useSelector} from 'react-redux';
import isEqual from 'lodash.isequal';
import {Button, Checkbox, FormControlLabel, FormGroup, MenuItem, Select} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import RestoreFilters from '@material-ui/icons/SettingsBackupRestoreOutlined';
import ExpandIcon from '@material-ui/icons/DoubleArrow';
import axios from 'axios';
import {tableConfig} from '../../../config/table-config';
import Progress from '../helpers/progress';
import CustomSearch from '../folder/custom-search';
import {categories, serviceConfig} from '../../../config/service-config';
import ScrollToTop from '../helpers/scroll-to-top';
import MassDeleteRuids from './mass-delete-ruid';
import UpdateAssetDialog from './update-asset-panel';
import RowContent from './row-content';

const SERVICE_KEY = 'ASSETS';
const SERVICE_KEY_SEARCH = 'ASSET';

const useStyles = makeStyles(() => ({
  dialogFooterText: {
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    marginLeft: 10,
    color: 'grey',
  },
  customFilters: {
    marginTop: 7,
  },
  restoreButton: {
    color: '#757575',
    borderRadius: '100%',
    minWidth: 48,
    minHeight: 48,
  },
  filterWarning: {
    padding: 10,
    marginTop: 7,
    color: 'grey',
  },
  topFilters: {display: 'flex', marginTop: 2, color: '#282828'},
  collapseStyle: {transform: 'rotate(90deg)'},
  categories: {height: 35, padding: 10, margin: 10, minWidth: 193},
  massDeleteButton: {margin: '10px 0'},
}));

const Asset = () => {
  const muiHandleSearch = useRef(null);
  const searchText = useRef(null);
  const classes = useStyles();
  const paramsDefault = {limit: 25, offset: 0, sortDirection: 1};
  const {
    params: {limit = 25, offset = 0, keyword = '', sortDirection = 1} = {},
    serviceContent,
    setParamValues,
    setParamValue,
    registerService,
    unregisterService,
  } = useService(SERVICE_KEY);

  const searchAsset = useService(SERVICE_KEY_SEARCH);
  const searchAssets = searchAsset.serviceContent;
  const fetching = useSelector(state => state?.service[SERVICE_KEY]?.fetching);
  const paramsService = useSelector(state => state?.service[SERVICE_KEY]?.params);
  const fetchingSearch = useSelector(state => state?.service[SERVICE_KEY_SEARCH]?.fetching);
  const paramsSearchService = useSelector(state => state?.service[SERVICE_KEY_SEARCH]?.params);
  const rippleRoles = GetRippleRoles();
  const {createToast} = useToast();
  const {assets, total} = serviceContent;
  const {headerConfig, columns} = tableConfig.ASSET;
  const {registerForm, setFieldValues, submitForm} = useForm(serviceConfig, SERVICE_KEY, 'update');
  const [shownAssets, setShownAssets] = useState([]);
  const [isSearchEnabled, setIsSearchEnabled] = useState(true);
  const [isFilterEnabled, setIsFilterEnabled] = useState(true);
  const [serverFilterList, setServerFilterList] = useState(columns.map(() => []));
  const [isSearchAsset, setIsSearchAsset] = useState(false);
  const [globalTotal, setGlobalTotal] = useState(0);
  const [assetApiFilterDisabled, setAssetApiFilterDisabled] = useState(false);
  const [isRecommended, setIsRecommended] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [findOnlyMissingEchostarId, setFindOnlyMissingEchostarId] = useState(false);
  const [expandedRows, setExpandedRows] = useState([]);
  const [expandCollapse, setExpandCollapse] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [updating, setUpdating] = useState(false);
  const [deletingDPs, setDeletingDPs] = useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [recordToUpdate, setRecordToUpdate] = useState({});

  const {
    closeOverlay: closeMassDeleteRuidsDialog,
    open: isMassDeleteRuidsDialogOpen,
    openOverlay: openMassDeleteRuidsDialog,
    overlayId: massDeleteRuidsDialogId,
  } = useOverlay({
    title: 'mass-delete-ruids',
  });

  const onRuidMassDelete = () => {
    registerService();
  };

  useEffect(() => {
    if (areRolesAuthorized(rippleRoles, 'ASSET', ['READ', 'UPDATE'])) {
      registerService();
      return () => {
        unregisterService();
        searchAsset.unregisterService();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (assets && !isSearchAsset) {
      setExpandedRows([]);
      setExpandCollapse(false);
      setShownAssets(assets);
      setGlobalTotal(total);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets]);

  useEffect(() => {
    if (searchAssets?.assets && isSearchAsset) {
      setExpandedRows([]);
      setExpandCollapse(false);
      setShownAssets(searchAssets?.assets);
      setGlobalTotal(searchAssets?.total);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchAssets?.assets]);

  useEffect(() => {
    if (searchAsset) {
      setShownAssets(searchAssets?.assets);
    } else {
      setShownAssets(assets);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchAsset]);

  const handleAssetUpdate = (asset, ruidsToDelete) => {
    const deleteDataPackages = (ruids = []) => {
      if (ruids.length < 1) return Promise.resolve(true);

      const {baseUrl, path, method} = serviceConfig.DELETE_DATA_PACKAGE;

      return axios[method.toLowerCase()](baseUrl + path, {data: ruids});
    };

    if (ruidsToDelete.length > 0) {
      setDeletingDPs(true);
    }

    deleteDataPackages(ruidsToDelete)
      .catch(error => {
        if (ruidsToDelete.length <= 0) {
          createToast(`Error while deleting data packages: ${error}`, {
            variant: 'error',
            persist: true,
          });
        }
      })
      .finally(() => {
        setDeletingDPs(false);
        return registerForm();
      })
      .then(() => setUpdating(true))
      .then(() => setFieldValues(asset))
      .then(() => submitForm(asset))
      .then(({error}) => {
        setUpdating(false);
        setIsUpdateDialogOpen(false);
        if (error) {
          createToast(error, {
            variant: 'error',
            persist: true,
            onClose: () => setIsUpdateDialogOpen(false),
          });
        } else {
          createToast('Successfully updated Asset.');
        }
      });
  };

  const handleAssetUpdateCancel = () => {
    setIsUpdateDialogOpen(false);
  };

  const renderExpandableRow = row => {
    const echostarId = row.length ? row[0] : null;
    if (echostarId) {
      const rowContent = !isSearchAsset
        ? assets.find(a => a.echostarId === echostarId)
        : searchAssets.assets.find(a => a.echostarId === echostarId);
      return <RowContent echostarId={echostarId} rowContent={rowContent} />;
    }
  };

  const revisePaging = tableState => {
    const {rowsPerPage, page} = tableState;
    const paramsPaging = {...(isSearchAsset ? paramsSearchService : paramsService)};
    paramsPaging.offset = rowsPerPage * page;
    paramsPaging.limit = rowsPerPage;

    if (!isSearchAsset) {
      setParamValues({
        expired: isExpired,
        recommended: isRecommended,
        findOnlyMissingEchostarId,
        keyword: searchText?.current?.value,
        ...paramsPaging,
      });
    } else {
      columns.forEach(({name}, index) => {
        if (serverFilterList[index].length) {
          [paramsPaging[name]] = serverFilterList[index];
        }
      });
      searchAsset.setParamValues(paramsPaging);
    }
  };

  const reviseSorting = (action, direction) => {
    let sortedShownAssets = [...shownAssets];

    if (action === 'echostarId') {
      if (direction === 'descending') {
        sortedShownAssets = [...shownAssets].sort((a, b) => a[action] - b[action]);
      } else {
        sortedShownAssets = [...shownAssets].sort((a, b) => b[action] - a[action]);
      }
    }

    if (action === 'name') {
      sortedShownAssets = [...shownAssets].sort((a, b) => {
        const nameA = a.name;
        const nameB = b.name;

        if (nameA < nameB) {
          return direction === 'descending' ? -1 : 1;
        }
        if (nameA > nameB) {
          return direction === 'descending' ? 1 : -1;
        }

        return 0;
      });
    }

    setShownAssets(sortedShownAssets);
  };

  const isKeywordEqual = isSearch =>
    isSearch &&
    ((!searchText.current && keyword === '') || (searchText.current && searchText.current.value === keyword));

  const updateSearch = (filterList, curOffset, isSearch, switchEngine = false) => () => {
    if (!isKeywordEqual(isSearch) || !isEqual(filterList, serverFilterList)) {
      setSelectedCategory(0);
      setIsExpired(false);
      setIsRecommended(false);
      setFindOnlyMissingEchostarId(false);
      const paramsSearch = {offset: curOffset, limit, sortDirection};
      columns.forEach(({name}, index) => {
        if (filterList[index].length) {
          [paramsSearch[name]] = filterList[index];
        }
      });
      if (searchText.current && searchText.current.value.length) {
        paramsSearch.keyword = searchText.current.value;
      }

      const searchActions = () => {
        setServerFilterList(filterList);
        if (isSearch) {
          // reset the mui page number to 0
          muiHandleSearch.current(searchText.current ? searchText.current.value : '');
        }
      };

      if (paramsSearch.keyword || switchEngine) {
        setParamValues(paramsSearch).then(() => searchActions());
        setIsSearchAsset(false);
      } else {
        searchAsset.setParamValues(paramsSearch).then(() => searchActions());
        setIsSearchAsset(true);
      }
    }
  };

  const setExpired = () => {
    setIsExpired(!isExpired);
    setParamValue('expired', !isExpired);
  };

  const setRecommended = () => {
    setIsRecommended(!isRecommended);
    setParamValue('recommended', !isRecommended);
  };

  const setMissingEids = () => {
    setFindOnlyMissingEchostarId(!findOnlyMissingEchostarId);
    setParamValue('findOnlyMissingEchostarId', !findOnlyMissingEchostarId);
  };

  const setDefaultSetOfAssetRecords = () => {
    setIsExpired(false);
    setIsRecommended(false);
    setFindOnlyMissingEchostarId(false);
    setIsSearchAsset(false);
  };

  const resetTableSearchFilters = (assetSearchDisable = false) => {
    if (assetSearchDisable) {
      updateSearch(
        serverFilterList.map(() => []),
        offset,
        false,
        true
      )();
    }
    setDefaultSetOfAssetRecords();
    setIsSearchEnabled(true);
    setAssetApiFilterDisabled(false);
    setSelectedCategory(0);
    setIsFilterEnabled(true);
    setParamValues(paramsDefault);
  };

  const renderCategories = () => (
    <Select
      className={classes.categories}
      disabled={assetApiFilterDisabled}
      value={selectedCategory}
      variant="outlined"
      onChange={cat => {
        const categoryTemp = cat.target.value;
        setSelectedCategory(categoryTemp);
        if (categoryTemp !== 0) {
          if (isSearchAsset) {
            searchAsset.setParamValue('category', categoryTemp);
          } else {
            setParamValue('category', categoryTemp);
          }
        } else {
          // eslint-disable-next-line no-unused-vars
          const {category, ...currentParams} = paramsService;
          if (isSearchAsset) {
            searchAsset.setParamValues(currentParams);
          } else {
            setParamValues(currentParams);
          }
        }
      }}
    >
      {categories.map((c, index) => (
        <MenuItem key={index} value={c.code}>
          {c.name}
        </MenuItem>
      ))}
    </Select>
  );

  const renderTopFilters = () => (
    <div className={classes.topFilters}>
      {renderCategories()}

      {assetApiFilterDisabled ? (
        <span className={classes.filterWarning}>Click Reset Search Filters button to enable Global filtering</span>
      ) : null}

      <FormControl className={classes.customFilters} component="fieldset" disabled={assetApiFilterDisabled}>
        <FormGroup row={true}>
          <FormControlLabel
            control={<Checkbox checked={isExpired} name="gilad" onChange={() => setExpired()} />}
            label="Include Expired"
          />
          <FormControlLabel
            control={<Checkbox checked={isRecommended} name="jason" onChange={() => setRecommended()} />}
            label="Recommended"
          />
          <FormControlLabel
            control={<Checkbox checked={findOnlyMissingEchostarId} name="antoine" onChange={() => setMissingEids()} />}
            label="Only Missing EIDs"
          />
        </FormGroup>
      </FormControl>

      <Button
        className={classes.massDeleteButton}
        color="primary"
        variant="contained"
        onClick={openMassDeleteRuidsDialog}
      >
        delete RUIDs batch
      </Button>
    </div>
  );

  const tableOptions = () => ({
    serverSide: true,
    sort: true,
    selectableRows: 'none',
    expandableRows: true,
    expandableRowsOnClick: true,
    rowsExpanded: expandedRows,
    rowsPerPage: 25,
    rowsPerPageOptions: [25, 50, 100, 300],
    count: globalTotal,
    jumpToPage: true,
    search: isSearchEnabled,
    filter: isFilterEnabled,
    viewColumns: false,
    responsive: 'vertical',
    customSearchRender: (_searchData, handleSearch, hideSearch) => {
      muiHandleSearch.current = handleSearch;
      return (
        <CustomSearch
          searchText={searchText}
          onHide={hideSearch}
          onSearch={() => updateSearch(serverFilterList, 0, true)()}
        />
      );
    },
    customFilterDialogFooter: filterList => (
      <div className={classes.dialogFooter}>
        <Button color="primary" size="small" variant="outlined" onClick={updateSearch(filterList, 0, false)}>
          Apply Filters
        </Button>
        <span className={classes.dialogFooterText}> Click outside of dialog to close</span>
      </div>
    ),
    customToolbar: () => (
      <>
        {isFilterEnabled && (
          <Button
            className={classes.restoreButton}
            title="Reset Search Filters"
            variant="text"
            onClick={() => resetTableSearchFilters()}
          >
            <RestoreFilters />
          </Button>
        )}
        <Button
          className={classes.restoreButton}
          title={`Click to ${expandCollapse ? 'Collapse' : 'Expand'} all rows`}
          variant="text"
          onClick={() => {
            setExpandedRows(!expandCollapse ? () => [...Array(limit).keys()].map(i => i) : []);
            setExpandCollapse(!expandCollapse);
          }}
        >
          <ExpandIcon
            className={classes[expandCollapse ? 'collapseStyle' : '']}
            onClick={() => {
              setExpandedRows(!expandCollapse ? () => [...Array(limit).keys()].map(i => i) : []);
              setExpandCollapse(!expandCollapse);
            }}
          />
        </Button>
      </>
    ),
    onFilterChange: (changedColumn, _filterList, type) => {
      if (type === 'reset') {
        resetTableSearchFilters(true);
      } else if (type === 'chip') {
        updateSearch(
          columns.map(({name}, index) => (name === changedColumn ? [] : serverFilterList[index])),
          0,
          false
        )();
      }
    },
    onColumnSortChange: (action, direction) => {
      reviseSorting(action, direction);
    },
    onTableChange: (action, tableState) => {
      switch (action) {
        case 'changePage':
        case 'changeRowsPerPage':
          revisePaging(tableState);
          break;
        case 'onSearchOpen':
          setIsRecommended(false);
          setIsExpired(false);
          setIsFilterEnabled(false);
          setIsSearchAsset(false);
          break;
        case 'onSearchClose':
          setIsFilterEnabled(true);
          setDefaultSetOfAssetRecords();
          searchText.current = undefined;
          setParamValues(paramsDefault);
          setSelectedCategory(0);
          break;
        case 'onFilterDialogOpen':
          setIsSearchEnabled(false);
          setAssetApiFilterDisabled(true);
          setSelectedCategory(0);
          break;
        default:
      }
    },
    renderExpandableRow,
  });

  const onSearchAssetUpdate = ({guid}) => {
    const {baseUrl, path, method} = serviceConfig.ASSET_BY_GUID;

    setUpdating(true);

    axios[method.toLowerCase()](`${baseUrl}${path.replace('{guid}', guid)}`)
      .then(({data: fetchRecord}) => {
        setRecordToUpdate(fetchRecord);
        setIsUpdateDialogOpen(true);
      })
      .catch(err => createToast(err.message, {variant: 'error'}))
      .finally(() => {
        setUpdating(false);
      });
  };

  return (
    <>
      {renderTopFilters()}
      <Overlay id={massDeleteRuidsDialogId} open={isMassDeleteRuidsDialogOpen} title="Mass Delete RUIDs">
        <MassDeleteRuids handleClose={closeMassDeleteRuidsDialog} onRuidMassDelete={onRuidMassDelete} />
      </Overlay>
      <CrudDataTable
        canUpdate={areRolesAuthorized(rippleRoles, 'ASSET', ['UPDATE'])}
        columnConfig={columns}
        data={shownAssets && shownAssets.length ? shownAssets.map(asset => ({...asset})) : []}
        headerConfig={headerConfig}
        idKey="echostarId"
        itemLabel="Asset"
        options={tableOptions()}
        title="Assets Management"
        onUpdate={echostarId => {
          const record = assets.find(asset => asset?.echostarId === echostarId);
          const searchRecord = searchAssets?.assets?.find(asset => asset?.echostarId === echostarId);
          if (record) {
            setRecordToUpdate(record);
            setIsUpdateDialogOpen(true);
          } else if (searchRecord) {
            if (!searchRecord.guid) {
              return createToast('Asset not found. Contact administrator if issue persists', {variant: 'error'});
            }
            onSearchAssetUpdate(searchRecord);
          }
        }}
      />
      {
        <UpdateAssetDialog
          handleCancel={handleAssetUpdateCancel}
          handleUpdate={handleAssetUpdate}
          isOpen={isUpdateDialogOpen}
          recordToUpdate={{...recordToUpdate}}
        />
      }
      {((!isSearchAsset && fetching) || (isSearchAsset && fetchingSearch)) && (
        <Progress title={assets && assets.length ? 'Assets Page' : 'Assets'} />
      )}
      {(updating || deletingDPs) && (
        <Progress
          replaceAllText={true}
          title={`${updating ? 'Updating Asset' : deletingDPs ? 'Deleting Data Packages' : null}`}
        />
      )}
      <ScrollToTop />
    </>
  );
};

export default Asset;
